/**
 * Feature Access Definitions
 * Single source of truth for tier-based feature gating.
 * Pure functions — no React, no DB. Used by both frontend and backend.
 */

import { ANALYSIS_MODES, type AnalysisMode } from './analysisMode';
import { type SubscriptionTier, TIER_DISPLAY_NAMES } from './subscriptionTier';

// =============================================================================
// Feature Identifiers
// =============================================================================

export const GATED_FEATURES = [
  // Views / Navigation
  'analytics_summary',
  'analytics_advanced',
  'lab_view',

  // AI Configuration
  'history_personal',
  'history_community',

  // Social
  'social_follow',
  'social_friend',
  'social_share',
  'social_profile_view',
  'leaderboard_rank',

  // Customization
  'theme_premium',

  // Tracking
  'tracking_unlimited',

  // Scoring & Analysis
  'scoring_breakdown',
  'scoring_deep_analysis',

  // Analysis Modes (visibility and badges; the server enforces modes by quota)
  'analysis_standard',
  'analysis_detailed',
  'analysis_premium',
  'lottery_ticket_tier',

  // Line Shopping
  'odds_comparison',

  // Scoring (Elite)
  'scoring_prediction_math',

  // Sharp Money (elite+admin, server-gated)
  'sharp_money_signals',
  'ev_badges',
  'line_movement_badges',
  'middles_alerts',

  // Tracked Parlay Monitoring
  'tracked_line_alerts',
  'tracked_sharp_signal_alerts',
  'tracked_injury_alerts',

  // Slate-wide Alerts (market intelligence)
  'slate_injury_alerts',
  'slate_line_movement_alerts',
  'slate_sharp_signal_alerts',

  // AI Chat Assistant
  'ai_chat',

  // ML (model output) and related analytics
  'ml_score_badge',
  'ml_recommendation',
  'ml_top_factors',
  'ml_full_factors',
  'ml_predicted_edge',
  'ml_score_lookup',
  'prop_pair_cohit',
  'ml_parlay_optimizer',
  'ml_generation_mode',
  'clv_per_pick',
  'clv_trends',
  'bankroll_simulation',
  'team_prop_hit_rates',

  // Market tab
  'ml_market_slate_overview',
  'ml_market_top_picks',
] as const;

export type GatedFeature = (typeof GATED_FEATURES)[number];

// =============================================================================
// Access Patterns (UI rendering hints)
// =============================================================================

/** How a feature appears for a given tier */
export type AccessPattern = 'allowed' | 'locked' | 'hidden';

// =============================================================================
// Feature Access Matrix
// =============================================================================

const FEATURE_ACCESS: Record<GatedFeature, Record<SubscriptionTier, AccessPattern>> = {
  // Views
  analytics_summary: {
    free: 'allowed',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  analytics_advanced: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  lab_view: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },

  // AI Configuration
  history_personal: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  history_community: {
    free: 'locked',
    pro: 'locked',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },

  // Social
  social_follow: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  social_friend: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  social_share: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  social_profile_view: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  leaderboard_rank: {
    free: 'hidden',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },

  // Customization
  theme_premium: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },

  // Tracking
  tracking_unlimited: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },

  // Scoring
  scoring_breakdown: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  scoring_deep_analysis: {
    free: 'locked',
    pro: 'locked',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  scoring_prediction_math: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'locked',
    elite: 'allowed',
    admin: 'allowed',
  },

  // Analysis Modes
  analysis_standard: {
    free: 'allowed',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  analysis_detailed: {
    free: 'locked',
    pro: 'locked',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  analysis_premium: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  lottery_ticket_tier: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },

  // Line Shopping
  odds_comparison: {
    free: 'locked',
    pro: 'allowed',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },

  // Sharp Money (elite+admin — server strips data for lower tiers)
  sharp_money_signals: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'allowed',
    admin: 'allowed',
  },
  ev_badges: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'allowed',
    admin: 'allowed',
  },
  line_movement_badges: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  middles_alerts: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'allowed',
    admin: 'allowed',
  },

  // Tracked Parlay Monitoring
  tracked_line_alerts: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  tracked_sharp_signal_alerts: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'allowed',
    admin: 'allowed',
  },
  tracked_injury_alerts: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },

  // Slate-wide Alerts (market intelligence)
  slate_injury_alerts: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  slate_line_movement_alerts: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'allowed',
    elite: 'allowed',
    admin: 'allowed',
  },
  slate_sharp_signal_alerts: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'allowed',
    admin: 'allowed',
  },

  // AI Chat Assistant — 'locked' so FAB shows with upgrade prompt
  ai_chat: {
    free: 'locked',
    pro: 'locked',
    premium: 'locked',
    elite: 'allowed',
    admin: 'allowed',
  },

  // ML Scoring — admin-only while ML pipeline is in progress
  ml_score_badge: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  ml_recommendation: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  ml_top_factors: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  ml_full_factors: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  ml_predicted_edge: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  ml_score_lookup: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  prop_pair_cohit: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  ml_parlay_optimizer: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  ml_generation_mode: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  clv_per_pick: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  clv_trends: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  bankroll_simulation: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  team_prop_hit_rates: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },

  // Market tab: admin-only while the ML pipeline is in progress
  ml_market_slate_overview: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
  ml_market_top_picks: {
    free: 'hidden',
    pro: 'hidden',
    premium: 'hidden',
    elite: 'hidden',
    admin: 'allowed',
  },
};

/** Get the UI access pattern for a tier + feature combination */
export function getAccessPattern(tier: SubscriptionTier, feature: GatedFeature): AccessPattern {
  return FEATURE_ACCESS[feature]?.[tier] ?? 'locked';
}

/** Check if a tier has access to a feature */
export function hasAccess(tier: SubscriptionTier, feature: GatedFeature): boolean {
  return getAccessPattern(tier, feature) === 'allowed';
}

/** Check if a feature is locked (visible but disabled, click shows upgrade) */
export function isFeatureLocked(tier: SubscriptionTier, feature: GatedFeature): boolean {
  return getAccessPattern(tier, feature) === 'locked';
}

/** Check if a feature is hidden (not rendered at all) */
export function isFeatureHidden(tier: SubscriptionTier, feature: GatedFeature): boolean {
  return getAccessPattern(tier, feature) === 'hidden';
}

/** The matrix key behind each analysis mode button. */
export const ANALYSIS_MODE_FEATURES: Record<AnalysisMode, GatedFeature> = {
  standard: 'analysis_standard',
  detailed: 'analysis_detailed',
  premium: 'analysis_premium',
};

/** Analysis modes a tier is shown: every mode whose gate is not Hidden for it (Locked still shows). */
export function visibleAnalysisModes(tier: SubscriptionTier): AnalysisMode[] {
  return ANALYSIS_MODES.filter((mode) => !isFeatureHidden(tier, ANALYSIS_MODE_FEATURES[mode]));
}

/** Why a tier cannot run a mode: a Locked mode names the plan that unlocks it, a Hidden one offers no upgrade. */
export function modeBlockedMessage(tier: SubscriptionTier, mode: AnalysisMode): string {
  const feature = ANALYSIS_MODE_FEATURES[mode];
  if (isFeatureLocked(tier, feature)) {
    return `${mode} mode needs the ${TIER_DISPLAY_NAMES[getRequiredTier(feature)]} plan.`;
  }
  return `${mode} mode is not available.`;
}

/** Get the minimum purchasable tier that unlocks a feature (for upgrade badge text) */
export function getRequiredTier(feature: GatedFeature): 'pro' | 'premium' | 'elite' {
  if (FEATURE_ACCESS[feature].pro === 'allowed') return 'pro';
  if (FEATURE_ACCESS[feature].premium === 'allowed') return 'premium';
  return 'elite';
}

// =============================================================================
// Theme Gating
// =============================================================================

const FREE_THEME_IDS = ['dark-electric', 'midnight-blue', 'sunset-amber'] as const;

/** Check if a theme is available on the free tier */
export function isThemeFree(themeId: string): boolean {
  return (FREE_THEME_IDS as readonly string[]).includes(themeId);
}

/** Get all free theme IDs */
export function getFreeThemeIds(): readonly string[] {
  return FREE_THEME_IDS;
}

// =============================================================================
// Tracking Limits
// =============================================================================

const TRACKING_LIMITS: Record<SubscriptionTier, number> = {
  free: 5,
  pro: 50,
  premium: 50,
  elite: 50,
  admin: 999,
};

/** Get daily tracking limit for a tier */
export function getTrackingLimit(tier: SubscriptionTier): number {
  return TRACKING_LIMITS[tier];
}

// =============================================================================
// Factor Toggle Limits
// =============================================================================

export const FACTOR_TOGGLE_LIMITS: Record<SubscriptionTier, number> = {
  free: 8,
  pro: 15,
  premium: 30,
  elite: 30,
  admin: Infinity,
};

// =============================================================================
// Display Names & Descriptions (for UpgradeModal)
// =============================================================================

export const FEATURE_DISPLAY_NAMES: Record<GatedFeature, string> = {
  analytics_summary: 'Analytics Summary',
  analytics_advanced: 'Advanced Analytics',
  lab_view: 'Lab',
  history_personal: 'Personal History',
  history_community: 'Community History',
  social_follow: 'Follow Users',
  social_friend: 'Add Friends',
  social_share: 'Share Parlays',
  social_profile_view: 'View Profiles',
  leaderboard_rank: 'Leaderboard Ranking',
  theme_premium: 'Premium Themes',
  tracking_unlimited: 'Extended Tracking (50/day)',
  scoring_breakdown: 'Scoring & Analysis',
  scoring_deep_analysis: 'Deep Scoring Analysis',
  scoring_prediction_math: 'Prediction Math Breakdown',
  analysis_standard: 'Standard Analysis',
  analysis_detailed: 'Detailed Analysis',
  analysis_premium: 'Premium Analysis',
  lottery_ticket_tier: 'Lottery Ticket Parlays',
  odds_comparison: 'Line Shopping',
  sharp_money_signals: 'Sharp Money Signals',
  ev_badges: '+EV Badges',
  line_movement_badges: 'Line Movement Badges',
  middles_alerts: 'Middles Alerts',
  tracked_line_alerts: 'Line Movement Alerts',
  tracked_sharp_signal_alerts: 'Sharp Signal Alerts',
  tracked_injury_alerts: 'Injury Alerts',
  slate_injury_alerts: 'Slate Injury Alerts',
  slate_line_movement_alerts: 'Slate Line Movement Alerts',
  slate_sharp_signal_alerts: 'Slate Sharp Signal Alerts',
  ai_chat: 'AI Chat Assistant',
  // ML features
  ml_score_badge: 'ML Score',
  ml_recommendation: 'ML Recommendation',
  ml_top_factors: 'ML Top Factors',
  ml_full_factors: 'ML Full Factor Breakdown',
  ml_predicted_edge: 'Predicted Value and Edge',
  ml_score_lookup: "Look Up a Prop's ML Score",
  prop_pair_cohit: 'Prop Pair Co-Hit Rate',
  ml_parlay_optimizer: 'ML Parlay Optimizer',
  ml_generation_mode: 'Generation Mode',
  clv_per_pick: 'Closing Line Value per Pick',
  clv_trends: 'Closing Line Value Trends',
  bankroll_simulation: 'Bankroll Simulation',
  team_prop_hit_rates: 'Team Prop Hit Rates',
  ml_market_slate_overview: 'Slate Overview',
  ml_market_top_picks: 'Top ML Picks',
};

export const FEATURE_DESCRIPTIONS: Record<GatedFeature, string> = {
  analytics_summary: 'View your overall betting performance summary.',
  analytics_advanced:
    'Access detailed breakdowns, strategy analysis, issue tracking, and market performance.',
  lab_view: 'Experimental features and tools.',
  history_personal:
    'The AI sees your past results by player and stat type when it picks. Once your tracked bets prove an edge over the AI, you also get your personal score.',
  history_community: 'Leverage community betting data to improve AI pick accuracy.',
  social_follow:
    'Follow bettors and unlock their shared profile stats — prop breakdown, recent bets, and performance details.',
  social_friend:
    "Send friend requests to connect with other bettors. Friends get mutual access to each other's profile data.",
  social_share: 'Share your parlays with the community.',
  social_profile_view: 'View detailed profiles of other bettors.',
  leaderboard_rank: 'Appear on the community leaderboard rankings.',
  theme_premium: 'Access all premium themes including team-inspired designs.',
  tracking_unlimited: 'Track up to 50 parlays per day. Free tier limited to 5.',
  scoring_breakdown:
    'See AI recommendations, top scoring factors, and pick confidence for every pick.',
  scoring_deep_analysis:
    'Full factor breakdown, conflict analysis, per-leg scoring, and player warnings for every pick.',
  scoring_prediction_math:
    "See the AI's step-by-step statistical reasoning — baseline projections, matchup adjustments, and edge calculations for every pick.",
  analysis_standard: 'Standard mode analyzes picks with the fast model.',
  analysis_detailed:
    'Unlock Detailed mode for deeper leg-by-leg AI analysis with enhanced reasoning.',
  analysis_premium: 'Premium mode analyzes picks with the most capable model.',
  lottery_ticket_tier: 'Include the Lottery Ticket tier: 5 to 8 legs at +1000 or longer.',
  odds_comparison: 'Compare odds across 6 sportsbooks to find the best line for every leg.',
  sharp_money_signals: 'See Pinnacle reference lines and sharp-vs-soft book gaps on every leg.',
  ev_badges: 'Identify positive expected value (+EV) opportunities based on Pinnacle pricing.',
  line_movement_badges: 'Track line movement direction and velocity for each prop.',
  middles_alerts: 'Get alerted to middle opportunities between sportsbooks.',
  tracked_line_alerts: 'Get notified when lines move on your tracked parlays.',
  tracked_sharp_signal_alerts:
    'Get notified when sharp signals (RLM, +EV, consensus) appear on your tracked parlays.',
  tracked_injury_alerts:
    'Get notified when players in your tracked parlays are listed as Out or Questionable.',
  slate_injury_alerts: "Get notified when any starter on tonight's games goes Out or Doubtful.",
  slate_line_movement_alerts:
    "Get notified when a prop's line moves 2+ points at one of your books on tonight's slate.",
  slate_sharp_signal_alerts:
    "Get notified when a big move on tonight's slate comes with a sharp read (reverse line movement, consensus).",
  ai_chat:
    'Chat with an AI betting analyst to analyze parlays, get player insights, manage your account, and navigate the app using natural language.',
  // ML features
  ml_score_badge: "The model's 0-100 score on picks and in chat answers.",
  ml_recommendation: "The model's Bet, Lean or Skip call on each pick.",
  ml_top_factors: "The three inputs that moved the model's score most on each pick.",
  ml_full_factors: 'Every model input and its contribution on a scored prop.',
  ml_predicted_edge: "The model's predicted stat and how far it sits from the line.",
  ml_score_lookup:
    "The model's latest score for an upcoming prop from the last scheduled scoring run.",
  prop_pair_cohit: 'How often two prop types hit together in the same game, across settled picks.',
  ml_parlay_optimizer:
    "Builds a set of parlays from chosen legs, or from the model's top Bet picks.",
  ml_generation_mode: 'Choose how picks are generated: by the AI, the model, or both.',
  clv_per_pick: 'Whether each settled pick beat the closing line, in chat.',
  clv_trends: 'Weekly average closing line value and how often picks beat the close.',
  bankroll_simulation:
    'Simulates how a bankroll grows or shrinks under the chosen staking settings.',
  team_prop_hit_rates:
    "How our settled picks have hit against each opponent and for each team's own players, by prop type and over/under, marked from avoid to target.",
  ml_market_slate_overview:
    "Tonight's game and prop counts, slate size, and how many props the model rates Bet.",
  ml_market_top_picks: "The props the model scores highest across tonight's games.",
};
